import React, { useState } from "react";
import { Badge, Button, Pagination, Table } from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { IoMdAdd, IoMdSearch } from "react-icons/io";
import { FaEdit, FaTrash } from "react-icons/fa";
import { FiBarChart2 } from "react-icons/fi";
import { MdLocalOffer } from "react-icons/md";
import moment from "moment";
import axios from "../../config/axiosInstance";
import { COUPONS } from "../../config/apiConfig";
import Drawer from "../Drawer";
import Modal from "../modals/Modal";
import CouponForm from "../forms/CouponForm";
import ModalDelete from "../modals/ModalDelete";
import EmptyTable from "./EmptyTable";
import { SkeletonTable } from "../crm/Skeletons";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const formatDiscount = (coupon) =>
  coupon.discountType === "FIXED"
    ? formatCurrency(coupon.discountValue)
    : `${Number(coupon.discountValue || 0)
        .toString()
        .replace(".", ",")}%`;

const formatDate = (value) =>
  value ? moment(value).format("DD/MM/YYYY") : "—";

const getErrorMessage = (error) =>
  error?.response?.data?.message ||
  error?.message ||
  "Não foi possível concluir a operação.";

const CouponsTable = () => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParam, setSearchParam] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [historyCoupon, setHistoryCoupon] = useState(null);
  const [formError, setFormError] = useState("");

  const { isLoading, data } = useQuery(
    ["platform-coupons", currentPage, searchParam],
    async () => {
      const { data: response } = await axios.get(
        `${COUPONS.LIST}?page=${currentPage}&search=${encodeURIComponent(
          searchParam,
        )}`,
      );

      return response.data;
    },
  );

  const { data: history, isLoading: isLoadingHistory } = useQuery(
    ["platform-coupon-history", historyCoupon?.id],
    async () => {
      const { data: response } = await axios.get(
        `${COUPONS.ONE}/${historyCoupon.id}/history`,
      );

      return response.data;
    },
    { enabled: !!historyCoupon?.id },
  );

  const coupons = data?.data || [];
  const summary = data?.summary || {};
  const totalPages = data?.totalPages || 1;

  const closeForm = () => {
    setIsFormOpen(false);
    setSelectedCoupon(null);
    setFormError("");
  };

  const closeHistory = () => setHistoryCoupon(null);

  const saveMutation = useMutation(
    async (payload) => {
      if (selectedCoupon?.id) {
        return axios.put(
          `${COUPONS.ONE}/${selectedCoupon.id}`,
          payload,
        );
      }

      return axios.post(COUPONS.LIST, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("platform-coupons");
        closeForm();
      },
      onError: (error) => setFormError(getErrorMessage(error)),
    },
  );

  const deleteMutation = useMutation(
    async (couponId) => axios.delete(`${COUPONS.ONE}/${couponId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("platform-coupons");
        setCouponToDelete(null);
      },
    },
  );

  const handleSearch = (event) => {
    event.preventDefault();
    setSearchParam(searchTerm);
    setCurrentPage(1);
  };

  const handleOpenForm = (coupon = null) => {
    setSelectedCoupon(coupon);
    setFormError("");
    setIsFormOpen(true);
  };

  const handleOpenHistory = (coupon) => setHistoryCoupon(coupon);

  const renderStatus = (coupon) => {
    if (!coupon.isActive) {
      return <Badge color="gray">Inativo</Badge>;
    }

    if (
      coupon.expiresAt &&
      moment(coupon.expiresAt).isBefore(moment(), "day")
    ) {
      return <Badge color="warning">Expirado</Badge>;
    }

    if (
      coupon.usageLimit !== null &&
      coupon.usageLimit !== undefined &&
      coupon.usageCount >= coupon.usageLimit
    ) {
      return <Badge color="warning">Esgotado</Badge>;
    }

    return <Badge color="success">Ativo</Badge>;
  };

  return (
    <>
      <div className="space-y-6">
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.4em] text-slate-400">
                Cupons
              </p>
              <h2 className="text-xl font-semibold text-slate-900">
                Cupons de assinatura
              </h2>
              <p className="text-sm text-slate-500">
                Descontos aplicados na primeira compra do plano, vinculados no
                cadastro da empresa.
              </p>
            </div>
            <Button onClick={() => handleOpenForm(null)}>
              <IoMdAdd className="mr-2 h-5 w-5" />
              Novo cupom
            </Button>
          </div>
        </section>

        <section className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
              Cupons
            </p>
            <p className="text-3xl font-bold text-slate-900">
              {summary.total ?? 0}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
              Ativos
            </p>
            <p className="text-3xl font-bold text-slate-900">
              {summary.active ?? 0}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
              Resgates
            </p>
            <p className="text-3xl font-bold text-slate-900">
              {summary.redemptions ?? 0}
            </p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
              Desconto dado
            </p>
            <p className="text-3xl font-bold text-slate-900">
              {formatCurrency(summary.discountTotal)}
            </p>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <form
            onSubmit={handleSearch}
            className="mb-4 flex items-center gap-3"
          >
            <div className="relative flex-1">
              <IoMdSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Buscar por código, nome ou parceiro"
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-10 pr-3 text-sm text-slate-900 transition placeholder:text-slate-400 focus:border-pink-500 focus:outline-none focus:ring-0"
              />
            </div>
            <Button type="submit" color="gray">
              Buscar
            </Button>
          </form>

          {isLoading ? (
            /*
              Esqueleto com a forma da tabela, não a frase "Carregando...".
              O texto não reserva o espaço do conteúdo, então a página saltava
              quando os cupons chegavam.
            */
            <SkeletonTable rows={5} cols={5} withAvatar={false} />
          ) : coupons.length === 0 ? (
            <EmptyTable icon={<MdLocalOffer />}>Nenhum cupom encontrado.</EmptyTable>
          ) : (
            <div className="overflow-x-auto">
              <Table hoverable>
                <Table.Head>
                  <Table.HeadCell>Código</Table.HeadCell>
                  <Table.HeadCell>Desconto</Table.HeadCell>
                  <Table.HeadCell>Parceiro</Table.HeadCell>
                  <Table.HeadCell>Usos</Table.HeadCell>
                  <Table.HeadCell>Validade</Table.HeadCell>
                  <Table.HeadCell>Status</Table.HeadCell>
                  <Table.HeadCell>
                    <span className="sr-only">Ações</span>
                  </Table.HeadCell>
                </Table.Head>
                <Table.Body className="divide-y">
                  {coupons.map((coupon) => (
                    <Table.Row key={coupon.id} className="bg-white">
                      <Table.Cell className="font-semibold text-slate-900">
                        {coupon.code}
                        {coupon.name && (
                          <span className="block text-xs font-normal text-slate-500">
                            {coupon.name}
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {formatDiscount(coupon)}
                        {coupon.firstPurchaseOnly && (
                          <span className="block text-xs text-slate-500">
                            1ª compra
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell>{coupon.partnerName || "—"}</Table.Cell>
                      <Table.Cell>
                        {coupon.usageCount}
                        {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ""}
                      </Table.Cell>
                      <Table.Cell>{formatDate(coupon.expiresAt)}</Table.Cell>
                      <Table.Cell>{renderStatus(coupon)}</Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            title="Ver resgates"
                            onClick={() => handleOpenHistory(coupon)}
                            className="text-slate-400 hover:text-slate-700"
                          >
                            <FiBarChart2 size={18} />
                          </button>
                          <button
                            type="button"
                            title="Editar"
                            onClick={() => handleOpenForm(coupon)}
                            className="text-slate-400 hover:text-slate-700"
                          >
                            <FaEdit size={16} />
                          </button>
                          <button
                            type="button"
                            title="Excluir"
                            onClick={() => setCouponToDelete(coupon)}
                            className="text-slate-400 hover:text-red-600"
                          >
                            <FaTrash size={15} />
                          </button>
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-4 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            </div>
          )}
        </section>
      </div>

      <Modal
        isOpen={isFormOpen}
        onClose={closeForm}
        size="lg"
        icon={MdLocalOffer}
        title={selectedCoupon?.id ? "Editar cupom" : "Novo cupom"}
        subtitle={
          selectedCoupon?.id
            ? selectedCoupon.code
            : "Desconto aplicado na primeira assinatura"
        }
        closeOnBackdrop={false}
        bodyClassName="bg-slate-50/50"
      >
        <CouponForm
          coupon={selectedCoupon}
          onSubmit={(payload) => saveMutation.mutate(payload)}
          onCancel={closeForm}
          isSaving={saveMutation.isLoading}
          errorMessage={formError}
        />
      </Modal>

      <Drawer
        isOpen={!!historyCoupon}
        onClose={closeHistory}
        className="w-full md:w-[560px]"
        title={
          historyCoupon ? `Resgates do cupom ${historyCoupon.code}` : "Resgates"
        }
      >
        {historyCoupon && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-200 p-4">
              <p className="text-xs uppercase tracking-[0.3em] text-slate-400">
                Desconto concedido
              </p>
              <p className="text-2xl font-bold text-slate-900">
                {formatCurrency(history?.summary?.discountTotal)}
              </p>
              <p className="text-sm text-slate-500">
                {history?.summary?.redemptions ?? 0} resgate(s)
              </p>
            </div>

            {isLoadingHistory ? (
              <p className="text-sm text-slate-500">Carregando resgates...</p>
            ) : (history?.data || []).length === 0 ? (
              <p className="text-sm text-slate-500">
                Este cupom ainda não foi usado.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {history.data.map((item) => (
                  <li key={item.id} className="py-3">
                    <p className="font-medium text-slate-900">
                      {item.companyName || `Empresa #${item.companyId}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {item.planId || "—"} · {item.billingCycle || "—"} ·{" "}
                      {formatDate(item.createdAt)}
                    </p>
                    <p className="text-sm text-slate-600">
                      {formatCurrency(item.totalBeforeDiscount)} →{" "}
                      <strong>{formatCurrency(item.totalAfterDiscount)}</strong>{" "}
                      (−{formatCurrency(item.discountAmount)})
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Drawer>

      <ModalDelete
        isOpen={!!couponToDelete}
        setOpenModal={() => setCouponToDelete(null)}
        isLoading={deleteMutation.isLoading}
        handleDelete={() => deleteMutation.mutate(couponToDelete.id)}
        title={`Excluir o cupom ${couponToDelete?.code}? Se ele já foi usado, será apenas desativado.`}
      />
    </>
  );
};

export default CouponsTable;
